import { Link } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { CircleArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type DigestPreviewRequest,
  type PortalDigestCadence,
  type PortalDigestPayload,
  useGetCrmPipelines,
  useGetDigestPreview,
  useGetDigestSettings,
  useSendTestDigest,
  useUpdateDigestSettings,
} from '~entities/portal-digest';
import { useGetAllSessionsInfinite } from '~entities/session/api/get-sessions';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const WEEKDAY_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Europe/London',
];

const getTimezoneOptions = (): Array<string> => {
  const intl = Intl as { supportedValuesOf?: (key: string) => Array<string> };
  const supported = intl.supportedValuesOf?.('timeZone');
  return supported && supported.length > 0 ? supported : FALLBACK_TIMEZONES;
};

/** Stop paging if the API keeps reporting more pages than this. */
const MAX_ACTIVE_SESSION_PAGES = 50;

export function DailyDigestPage() {
  const settingsQuery = useGetDigestSettings();
  const updateSettings = useUpdateDigestSettings();
  const sendTest = useSendTestDigest();
  const { data: studioDetails } = useGetStudioDetails();
  const {
    data: sessionsInfinite,
    isLoading: sessionsLoading,
    isError: sessionsIsError,
    hasNextPage: hasMoreSessions,
    isFetchingNextPage: isFetchingMoreSessions,
    fetchNextPage: fetchMoreSessions,
  } = useGetAllSessionsInfinite({ isArchive: false, limit: 100 });
  const {
    data: pipelinesData,
    isLoading: pipelinesLoading,
    isError: pipelinesIsError,
    isFetching: pipelinesFetching,
    refetch: refetchPipelines,
  } = useGetCrmPipelines();

  const [cadence, setCadence] = useState<PortalDigestCadence>('off');
  const [weekday, setWeekday] = useState('1');
  const [sendTime, setSendTime] = useState('08:00');
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [sessionIds, setSessionIds] = useState<Array<string>>([]);
  const [crmPipelineId, setCrmPipelineId] = useState('');
  const [emails, setEmails] = useState<Array<string>>([]);
  const [phones, setPhones] = useState<Array<string>>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  // Last Preview request: the form values it was built with plus a sequence
  // number so repeat clicks always refetch. Null until first click.
  const [previewRequest, setPreviewRequest] =
    useState<DigestPreviewRequest | null>(null);
  const previewQuery = useGetDigestPreview(previewRequest);

  // Saved settings hydrate the form once. A later background refetch (a
  // save invalidation or window focus) must not overwrite unsaved edits.
  const settingsHydrated = useRef(false);
  useEffect(() => {
    const settings = settingsQuery.data;
    if (!settings || settingsHydrated.current) return;
    settingsHydrated.current = true;
    setCadence(settings.cadence);
    setWeekday(String(settings.weekday));
    setSendTime(settings.sendTime);
    setTimezone(settings.timezone);
    setSessionIds(settings.sessionIds);
    setCrmPipelineId(settings.crmPipelineId);
    setEmails(settings.emails);
    setPhones(settings.phones);
  }, [settingsQuery.data]);

  const timezoneOptions = useMemo(() => {
    const options = getTimezoneOptions();
    // Controlled select: the saved value must always have a matching option.
    if (timezone && !options.includes(timezone)) {
      return [timezone, ...options];
    }
    return options;
  }, [timezone]);

  const loadedSessionPages = sessionsInfinite?.pages.length ?? 0;
  const reachedSessionPageCap = loadedSessionPages >= MAX_ACTIVE_SESSION_PAGES;

  useEffect(() => {
    if (
      hasMoreSessions &&
      !isFetchingMoreSessions &&
      !sessionsIsError &&
      !reachedSessionPageCap
    ) {
      void fetchMoreSessions();
    }
  }, [
    fetchMoreSessions,
    hasMoreSessions,
    isFetchingMoreSessions,
    reachedSessionPageCap,
    sessionsIsError,
  ]);

  const sessions = useMemo(() => {
    const seen = new Set<string>();
    const list: Array<{ _id: string; name: string }> = [];
    for (const page of sessionsInfinite?.pages ?? []) {
      for (const session of page.sessions) {
        if (seen.has(session._id)) continue;
        seen.add(session._id);
        list.push({ _id: session._id, name: session.name });
      }
    }
    return list;
  }, [sessionsInfinite?.pages]);

  const pipelines = pipelinesData?.pipelines ?? [];
  const pipelinesUnavailable =
    pipelinesData?.unavailable === true || pipelinesIsError;

  const is404 =
    settingsQuery.isError &&
    settingsQuery.error instanceof AxiosError &&
    settingsQuery.error.response?.status === 404;
  // 404 means the digest API is not deployed yet and renders its own state.
  // Any other failure with no loaded settings must not leave hard-coded
  // defaults saveable. A later refetch error keeps the last successful payload.
  const settingsLoadFailed =
    settingsQuery.isError && !is404 && settingsQuery.data === undefined;
  const settingsLocked = settingsQuery.isLoading || settingsLoadFailed;

  const sessionsPaginationSettled =
    !sessionsLoading &&
    !isFetchingMoreSessions &&
    (!hasMoreSessions || sessionsIsError || reachedSessionPageCap);

  const orphanSessionIds = useMemo(() => {
    if (!sessionsPaginationSettled) return [];
    const known = new Set(sessions.map((session) => session._id));
    return sessionIds.filter((id) => !known.has(id));
  }, [sessionIds, sessions, sessionsPaginationSettled]);

  const toggleSession = (sessionId: string, checked: boolean) => {
    setSessionIds((prev) =>
      checked ? [...prev, sessionId] : prev.filter((id) => id !== sessionId),
    );
  };

  const addEmail = () => {
    const next = emailDraft.trim().toLowerCase();
    if (!next) return;
    if (!EMAIL_PATTERN.test(next)) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (emails.includes(next)) {
      toast.error('That email is already in the list.');
      return;
    }
    setEmails((prev) => [...prev, next]);
    setEmailDraft('');
  };

  const addPhone = () => {
    const next = phoneDraft.trim();
    if (!next) return;
    if (phones.includes(next)) {
      toast.error('That phone number is already in the list.');
      return;
    }
    setPhones((prev) => [...prev, next]);
    setPhoneDraft('');
  };

  const handleSave = () => {
    if (settingsLoadFailed || settingsQuery.data === undefined) return;
    if (cadence !== 'off' && emails.length === 0) {
      toast.error('Add at least one recipient email to enable the digest.');
      return;
    }
    updateSettings.mutate(
      {
        cadence,
        weekday: Number(weekday),
        sendTime,
        timezone,
        sessionIds,
        crmPipelineId,
        emails,
        phones,
      },
      {
        onSuccess: () => toast.success('Digest settings saved.'),
        onError: (error) => {
          const message =
            error instanceof AxiosError
              ? ((error.response?.data as { message?: string }).message ??
                error.message)
              : 'Failed to save digest settings.';
          toast.error(message);
        },
      },
    );
  };

  // Unsaved on-screen values sent to preview/test-send. The backend merges
  // them over the saved settings without persisting anything.
  const currentDigestParams = () => ({
    cadence,
    weekday: Number(weekday),
    sendTime,
    timezone,
    sessionIds,
    crmPipelineId,
  });

  const handlePreview = () => {
    // Same protection as Save/Send test: previewing before settings load
    // (or after a load failure) would post the hard-coded initial state.
    if (settingsLocked) return;
    setPreviewRequest((previous) => ({
      params: currentDigestParams(),
      seq: (previous?.seq ?? 0) + 1,
    }));
  };

  // Preview data comes straight from the query, so every refetch (Preview
  // click, window focus, cache invalidation) refreshes the card instead of
  // leaving a stale snapshot on screen.
  const preview = previewQuery.data?.digest ?? null;

  // Refetch the displayed preview when the page regains focus so live counts
  // (for example unread CRM conversations) are not stale between visits.
  const refetchPreview = previewQuery.refetch;
  useEffect(() => {
    if (!previewRequest) return;
    const onWindowFocus = () => {
      void refetchPreview();
    };
    window.addEventListener('focus', onWindowFocus);
    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [previewRequest, refetchPreview]);

  const handleSendTest = () => {
    if (settingsLocked) return;
    sendTest.mutate(
      { ...currentDigestParams(), emails },
      {
        onSuccess: (result) => {
          if (result.sent.length > 0) {
            toast.success(`Test digest sent to ${result.sent.join(', ')}.`);
          }
          for (const failure of result.failed) {
            toast.error(`Failed to send to ${failure.email}: ${failure.error}`);
          }
          if (result.sent.length === 0 && result.failed.length === 0) {
            toast.error('No recipients were emailed.');
          }
        },
        onError: (error) => {
          const message =
            error instanceof AxiosError
              ? ((error.response?.data as { message?: string }).message ??
                error.message)
              : 'Test send failed.';
          toast.error(message);
        },
      },
    );
  };

  if (is404) {
    return (
      <div className='p-4'>
        <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <span className='text-lg font-semibold'>Back to Dashboard</span>
        </Link>
        <Card className='mx-auto max-w-4xl'>
          <CardHeader>
            <CardTitle>Daily Digest</CardTitle>
            <CardDescription>
              Daily Digest is not available yet. Check back after the next
              platform update.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className='p-4'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>

      <Card className='mx-auto max-w-4xl'>
        <CardHeader>
          <CardTitle>Daily Digest</CardTitle>
          <CardDescription>
            Email yourself (and staff) a {''}
            {cadence === 'weekly' ? 'weekly' : 'daily'} snapshot of enrollments,
            unread CRM conversations, one CRM pipeline, and open failed
            payments. Powered by your studio timezone{' '}
            {studioDetails?.timezone ? `(${studioDetails.timezone})` : ''} by
            default.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          {settingsLoadFailed ? (
            <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3'>
              <p className='text-sm text-destructive'>
                Digest settings could not be loaded. Saving stays off until they
                load successfully, so existing settings are not replaced with
                defaults.
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => {
                  void settingsQuery.refetch();
                }}
                disabled={settingsQuery.isFetching}
              >
                {settingsQuery.isFetching ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Retrying
                  </>
                ) : (
                  'Retry'
                )}
              </Button>
            </div>
          ) : null}
          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='digest-cadence'>Cadence</Label>
              <Select
                value={cadence}
                onValueChange={(value) => {
                  setCadence(value as PortalDigestCadence);
                }}
                disabled={settingsLocked}
              >
                <SelectTrigger id='digest-cadence'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='off'>Off</SelectItem>
                  <SelectItem value='daily'>Daily</SelectItem>
                  <SelectItem value='weekly'>Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {cadence === 'weekly' ? (
              <div className='space-y-2'>
                <Label htmlFor='digest-weekday'>Send on</Label>
                <Select
                  value={weekday}
                  onValueChange={setWeekday}
                  disabled={settingsLocked}
                >
                  <SelectTrigger id='digest-weekday'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div />
            )}
            <div className='space-y-2'>
              <Label htmlFor='digest-time'>Send time</Label>
              <Input
                id='digest-time'
                type='time'
                value={sendTime}
                onChange={(event) => {
                  setSendTime(event.target.value);
                }}
                disabled={settingsLocked}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='digest-timezone'>Timezone</Label>
              <Select
                value={timezone}
                onValueChange={setTimezone}
                disabled={settingsLocked}
              >
                <SelectTrigger id='digest-timezone'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className='max-h-64'>
                  {timezoneOptions.map((zone) => (
                    <SelectItem key={zone} value={zone}>
                      {zone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label>Sessions included in enrollment metrics</Label>
            <p className='text-xs text-muted-foreground'>
              Enrollment, capacity, drops, and trial numbers are calculated
              across the selected sessions.
            </p>
            <div className='max-h-56 space-y-2 overflow-y-auto rounded-lg border bg-card p-4'>
              {sessionsLoading ? (
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <Loader2 className='size-4 animate-spin' /> Loading sessions…
                </div>
              ) : (
                <>
                  {sessions.map((session) => (
                    <label
                      key={session._id}
                      className='flex items-center gap-2 text-sm'
                    >
                      <Checkbox
                        checked={sessionIds.includes(session._id)}
                        disabled={settingsLocked}
                        onCheckedChange={(checked) => {
                          toggleSession(session._id, checked === true);
                        }}
                      />
                      <span>{session.name}</span>
                    </label>
                  ))}
                  {orphanSessionIds.map((sessionId) => (
                    <label
                      key={sessionId}
                      className='flex items-center gap-2 text-sm'
                    >
                      <Checkbox
                        checked
                        disabled={settingsLocked}
                        onCheckedChange={(checked) => {
                          toggleSession(sessionId, checked === true);
                        }}
                      />
                      <span>
                        Saved session
                        <span className='ml-1 font-mono text-xs text-muted-foreground'>
                          {sessionId}
                        </span>
                      </span>
                    </label>
                  ))}
                  {sessions.length === 0 && orphanSessionIds.length === 0 ? (
                    <p className='text-sm text-muted-foreground'>
                      {sessionsIsError
                        ? 'Sessions could not be loaded.'
                        : 'No active sessions found.'}
                    </p>
                  ) : null}
                  {isFetchingMoreSessions ? (
                    <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                      <Loader2 className='size-4 animate-spin' /> Loading more
                      sessions…
                    </div>
                  ) : null}
                  {sessionsIsError &&
                  (sessions.length > 0 || orphanSessionIds.length > 0) ? (
                    <p className='text-xs text-destructive'>
                      Some sessions could not be loaded. Saved sessions stay
                      listed so you can remove them.
                    </p>
                  ) : null}
                  {reachedSessionPageCap && hasMoreSessions ? (
                    <p className='text-xs text-muted-foreground'>
                      Showing the first {sessions.length} active sessions. Saved
                      sessions that are not in this list stay selected so you
                      can remove them.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='digest-pipeline'>CRM pipeline</Label>
            <p className='text-xs text-muted-foreground'>
              The digest shows the open opportunity count for each stage of one
              pipeline.
            </p>
            <Select
              value={crmPipelineId}
              onValueChange={setCrmPipelineId}
              disabled={
                settingsLocked || pipelinesLoading || pipelinesUnavailable
              }
            >
              <SelectTrigger id='digest-pipeline'>
                <SelectValue placeholder='Select a pipeline' />
              </SelectTrigger>
              <SelectContent>
                {crmPipelineId &&
                !pipelines.some((p) => p.id === crmPipelineId) ? (
                  <SelectItem value={crmPipelineId}>
                    Saved pipeline (not found in CRM)
                  </SelectItem>
                ) : null}
                {pipelines.map((pipeline) => (
                  <SelectItem key={pipeline.id} value={pipeline.id}>
                    {pipeline.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pipelinesUnavailable ? (
              <div className='flex flex-wrap items-center gap-2'>
                <p className='text-xs text-destructive'>
                  {pipelinesData?.error ??
                    'CRM pipelines could not be loaded. Reconnect Enrollio CRM and try again.'}
                </p>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    void refetchPipelines();
                  }}
                  disabled={pipelinesFetching}
                >
                  {pipelinesFetching ? (
                    <>
                      <Loader2 className='mr-2 size-4 animate-spin' />
                      Retrying
                    </>
                  ) : (
                    'Retry'
                  )}
                </Button>
              </div>
            ) : null}
          </div>

          <div className='grid gap-4 sm:grid-cols-2'>
            <RecipientEditor
              id='digest-email'
              label='Email recipients'
              description='Required. Each address gets the digest email.'
              placeholder='owner@studio.com'
              values={emails}
              draft={emailDraft}
              onDraftChange={setEmailDraft}
              onAdd={addEmail}
              onRemove={(value) => {
                setEmails((prev) => prev.filter((e) => e !== value));
              }}
              disabled={settingsLocked}
            />
            <RecipientEditor
              id='digest-phone'
              label='Phone recipients'
              description='Saved for later — SMS delivery is not live yet. Email still sends today.'
              placeholder='+1 555 123 4567'
              values={phones}
              draft={phoneDraft}
              onDraftChange={setPhoneDraft}
              onAdd={addPhone}
              onRemove={(value) => {
                setPhones((prev) => prev.filter((p) => p !== value));
              }}
              disabled={settingsLocked}
            />
          </div>

          <div className='flex flex-wrap justify-between gap-3 border-t pt-4'>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                onClick={handlePreview}
                disabled={settingsLocked || previewQuery.isFetching}
              >
                {previewQuery.isFetching ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Building preview
                  </>
                ) : (
                  'Preview'
                )}
              </Button>
              <Button
                variant='outline'
                onClick={handleSendTest}
                disabled={
                  settingsLocked || sendTest.isPending || emails.length === 0
                }
              >
                {sendTest.isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Sending test
                  </>
                ) : (
                  'Send test email'
                )}
              </Button>
            </div>
            <Button
              onClick={handleSave}
              disabled={settingsLocked || updateSettings.isPending}
            >
              {updateSettings.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving
                </>
              ) : (
                'Save Digest Settings'
              )}
            </Button>
          </div>

          {previewQuery.isError ? (
            <p className='text-sm text-destructive'>
              {previewQuery.error instanceof AxiosError &&
              previewQuery.error.response?.status === 404
                ? 'Preview is not available yet. Deploy the backend first.'
                : 'Could not build the digest preview.'}
            </p>
          ) : preview ? (
            <DigestPreviewCard digest={preview} />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function RecipientEditor({
  id,
  label,
  description,
  placeholder,
  values,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  disabled = false,
}: {
  id: string;
  label: string;
  description: string;
  placeholder: string;
  values: Array<string>;
  draft: string;
  onDraftChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className='space-y-2'>
      <Label htmlFor={id}>{label}</Label>
      <p className='text-xs text-muted-foreground'>{description}</p>
      <div className='flex gap-2'>
        <Input
          id={id}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => {
            onDraftChange(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onAdd();
            }
          }}
        />
        <Button
          type='button'
          variant='outline'
          size='icon'
          onClick={onAdd}
          disabled={disabled}
        >
          <Plus className='size-4' />
        </Button>
      </div>
      {values.length > 0 ? (
        <div className='flex flex-wrap gap-2 pt-1'>
          {values.map((value) => (
            <Badge key={value} variant='secondary' className='gap-1'>
              {value}
              <button
                type='button'
                aria-label={`Remove ${value}`}
                disabled={disabled}
                onClick={() => {
                  onRemove(value);
                }}
              >
                <X className='size-3' />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DigestPreviewCard({ digest }: { digest: PortalDigestPayload }) {
  const enrollment = digest.enrollment;
  return (
    <div className='space-y-4 rounded-lg border bg-muted/30 p-4'>
      <div>
        <p className='text-sm font-semibold'>
          Preview — generated{' '}
          {new Date(digest.generatedAt).toLocaleString(undefined, {
            timeZone: digest.timezone,
          })}{' '}
          ({digest.timezone})
        </p>
        <p className='text-xs text-muted-foreground'>
          {digest.cadence === 'weekly' ? 'Weekly' : 'Daily'} window:{' '}
          {new Date(digest.windowStart).toLocaleString(undefined, {
            timeZone: digest.timezone,
          })}{' '}
          →{' '}
          {new Date(digest.windowEnd).toLocaleString(undefined, {
            timeZone: digest.timezone,
          })}
        </p>
      </div>

      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Enrollment
        </p>
        {enrollment.available ? (
          <ul className='text-sm'>
            <li>Sessions: {enrollment.sessionNames?.join(', ') ?? '—'}</li>
            <li>
              Active enrolled students: {enrollment.activeEnrolledStudents ?? 0}
            </li>
            <li>
              Classes at capacity: {enrollment.classesFull ?? 0}/
              {enrollment.classesTotal ?? 0} ({enrollment.classesFullPct ?? 0}
              %)
            </li>
            <li>Near capacity (≥90%): {enrollment.classesNearCapacity ?? 0}</li>
            <li>
              Drops executed / scheduled: {enrollment.dropsExecuted ?? 0} /{' '}
              {enrollment.dropsScheduled ?? 0}
            </li>
            <li>New trial bookings: {enrollment.trialsBooked ?? 0}</li>
          </ul>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {enrollment.reason ?? 'N/A'}
          </p>
        )}
      </div>

      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Needs a response
        </p>
        {digest.needsResponse.available ? (
          <p className='text-sm'>
            Unread CRM conversations:{' '}
            {digest.needsResponse.unreadConversations ?? 0}
          </p>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {digest.needsResponse.error ?? 'N/A'}
          </p>
        )}
      </div>

      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          CRM pipeline
        </p>
        {digest.pipeline.available ? (
          <ul className='text-sm'>
            <li className='font-medium'>{digest.pipeline.pipelineName}</li>
            {(digest.pipeline.stages ?? []).map((stage) => (
              <li key={stage.stageId}>
                {stage.name}: {stage.openCount ?? 'N/A'}
              </li>
            ))}
            <li>
              Total open:{' '}
              {digest.pipeline.totalOpen === null
                ? 'N/A'
                : digest.pipeline.totalOpen}
            </li>
          </ul>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {digest.pipeline.error ?? 'No CRM pipeline selected.'}
          </p>
        )}
      </div>

      <div className='space-y-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Failed payments
        </p>
        {digest.failedPayments.count === 0 ? (
          <p className='text-sm'>No open failed payments.</p>
        ) : (
          <ul className='text-sm'>
            <li>
              {digest.failedPayments.count} open —{' '}
              {digest.failedPayments.currency}{' '}
              {digest.failedPayments.totalAmount.toFixed(2)}
            </li>
            {digest.failedPayments.parents.map((parent) => (
              <li key={parent.name}>
                {parent.name}
                {parent.invoiceCount > 1
                  ? ` ×${parent.invoiceCount}`
                  : ''}: {digest.failedPayments.currency}{' '}
                {parent.amount.toFixed(2)}
              </li>
            ))}
            {digest.failedPayments.truncatedCount > 0 ? (
              <li>
                <Link
                  to='/payment/failed-payments'
                  className='text-primary underline-offset-4 hover:underline'
                >
                  View {digest.failedPayments.truncatedCount} more in Failed
                  Payments
                </Link>
              </li>
            ) : null}
          </ul>
        )}
      </div>

      {digest.warnings.length > 0 ? (
        <div className='space-y-1 border-t pt-3'>
          <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
            Notes
          </p>
          {digest.warnings.map((warning) => (
            <p key={warning} className='text-xs text-muted-foreground'>
              {warning}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
